import React, { useState, useEffect } from 'react';
import notificationAPI from '../services/notificationAPI';
import RetroInput from './ui/RetroInput';
import RetroCheckbox from './ui/RetroCheckbox';
import './NotificationSettings.css';

function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications_enabled: false,
    discord_notifications_enabled: false,
    discord_user_id: '',
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const data = await notificationAPI.getPreferences();
      setPreferences({
        email_notifications_enabled: data.email_notifications_enabled || false,
        discord_notifications_enabled: data.discord_notifications_enabled || false,
        discord_user_id: data.discord_user_id || '',
      });
    } catch (error) {
      console.error('Failed to load preferences:', error);
      setErrors({ load: 'Einstellungen konnten nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  };

  const validateDiscordId = (id) => {
    if (!id) return true; // Empty is valid (user wants to unlink)

    // Must be 17-19 digits
    const discordIdRegex = /^\d{17,19}$/;
    return discordIdRegex.test(id);
  };

  const handleToggleEmail = (e) => {
    setPreferences({
      ...preferences,
      email_notifications_enabled: e.target.checked,
    });
    setErrors({});
    setSuccessMessage('');
  };

  const handleToggleDiscord = (e) => {
    setPreferences({
      ...preferences,
      discord_notifications_enabled: e.target.checked,
    });
    setErrors({});
    setSuccessMessage('');
  };

  const handleDiscordIdChange = (e) => {
    setPreferences({
      ...preferences,
      discord_user_id: e.target.value,
    });
    setErrors({});
    setSuccessMessage('');
  };

  const handleSave = async () => {
    // Validate
    const newErrors = {};

    if (preferences.discord_notifications_enabled && !preferences.discord_user_id) {
      newErrors.discord_user_id = 'Discord User ID ist erforderlich wenn Discord-Benachrichtigungen aktiviert sind';
    } else if (preferences.discord_user_id && !validateDiscordId(preferences.discord_user_id)) {
      newErrors.discord_user_id = 'Ungültige Discord User ID (muss 17-19 Ziffern sein)';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Save
    setSaving(true);
    setErrors({});
    setSuccessMessage('');

    try {
      await notificationAPI.updatePreferences({
        email_notifications_enabled: preferences.email_notifications_enabled,
        discord_notifications_enabled: preferences.discord_notifications_enabled,
        discord_user_id: preferences.discord_user_id || null,
      });

      setSuccessMessage('Einstellungen erfolgreich gespeichert');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setErrors({ save: 'Fehler beim Speichern der Einstellungen' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-icon">[n]</span>
            BENACHRICHTIGUNGEN
          </h2>
        </div>
        <p className="notification-loading">Laden...</p>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-icon">[n]</span>
          BENACHRICHTIGUNGEN
        </h2>
      </div>

      <p className="notification-settings-subtitle">
        Verwalte deine Event-Benachrichtigungen
      </p>

      {errors.load && (
        <div className="notification-error-banner">
          <span className="error-icon">[!]</span>
          {errors.load}
        </div>
      )}

      {successMessage && (
        <div className="notification-success-banner">
          <span className="success-icon">[✓]</span>
          {successMessage}
        </div>
      )}

      {/* Email Notifications */}
      <div className="notification-option">
        <RetroCheckbox
          id="email-notifications"
          checked={preferences.email_notifications_enabled}
          onChange={handleToggleEmail}
          label="Email-Benachrichtigungen aktivieren"
        />
        <p className="notification-option-hint">
          Erhalte eine Email wenn du zu einem Event eingeladen wirst
        </p>
      </div>

      {/* Discord Notifications */}
      <div className="notification-option">
        <RetroCheckbox
          id="discord-notifications"
          checked={preferences.discord_notifications_enabled}
          onChange={handleToggleDiscord}
          label="Discord-Benachrichtigungen aktivieren"
        />
        <p className="notification-option-hint">
          Erhalte eine Discord DM wenn du zu einem Event eingeladen wirst
        </p>

        {/* Discord ID Input - Only show if Discord enabled */}
        {preferences.discord_notifications_enabled && (
          <div className="discord-id-input-wrapper">
            <RetroInput
              id="discord-user-id"
              name="discord_user_id"
              type="text"
              value={preferences.discord_user_id}
              onChange={handleDiscordIdChange}
              placeholder="123456789012345678"
              label="Discord User ID"
              hint="Rechtsklick auf deinen Namen in Discord → Profil → User ID kopieren (Entwicklermodus erforderlich)"
              error={errors.discord_user_id}
              required
            />
          </div>
        )}
      </div>

      {errors.save && (
        <div className="notification-error">
          <span className="error-icon">[!]</span>
          {errors.save}
        </div>
      )}

      <div className="notification-actions">
        <button
          className="retro-button retro-button-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'SPEICHERE...' : 'SPEICHERN'}
        </button>
      </div>
    </div>
  );
}

export default NotificationSettings;
